import { displayReceiptNumber } from './displayReceiptNumber.js';
import { formatAdminInr } from './formatAdminPrice.js';
import { buildRegistrationCommentLines } from './adminCommentHistory.js';
import { extractRazorpayToken, razorpayDashboardUrl } from './razorpayDashboardUrl.js';
import { findBookingReceiptForLegacy, legacyReceiptToPrintableRow } from './openAdminReceiptPrint.js';
import { buildParticipantPaidByIndex } from './participantPaymentShare.js';

function paymentLineBelongsToParticipant(line, participantName) {
  const name = normalizeHoverPersonName(participantName);
  if (!name) return true;

  const label = normalizeHoverPersonName(line?.participantLabel);
  if (label) {
    const names = String(line?.participantLabel || '')
      .split(',')
      .map((n) => normalizeHoverPersonName(n))
      .filter(Boolean);
    if (names.length > 1) return names.includes(name);
    if (names.length === 1) return names[0] === name;
  }

  const blob = `${line?.detail || ''} ${line?.receiptNo || ''}`;
  if (!/participant\s*:/i.test(blob)) return true;
  const lower = blob.toLowerCase();
  return (
    lower.includes(`participant: ${name}`) ||
    lower.includes(`participant:${name}`) ||
    // Group Razorpay notes: "Participants: A, B"
    new RegExp(`participants:\\s*[^|]*\\b${escapeRegExp(name)}\\b`, 'i').test(blob)
  );
}

function normalizeHoverPersonName(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Payment tooltip lines for one participant row (cash/QR tagged receipts stay on that member). */
export function buildRegistrationPaymentHoverLinesForParticipant(booking, traveler, travelerIndex) {
  const lines = buildRegistrationPaymentHoverLines(booking);
  if (!lines.length) return lines;

  const idx = Number.isFinite(travelerIndex) ? travelerIndex : 0;
  const { names } = buildParticipantPaidByIndex(booking);
  const participantName = names[idx] || '';
  const filtered = lines.filter((line) => paymentLineBelongsToParticipant(line, participantName));

  // After a name edit, receipt notes may still say "Participant: Old Name".
  // Don't hide all payment history for that booking.
  if (filtered.length) return filtered;
  const travelerCount = Math.max(
    Number(booking?.travelerCount) || 0,
    Array.isArray(booking?.travelers) ? booking.travelers.length : 0,
    1
  );
  if (travelerCount <= 1) return lines;
  const groupOrUntagged = lines.filter((line) => {
    const label = String(line?.participantLabel || '').trim();
    if (!label) return true;
    if (label.includes(',')) return true;
    return false;
  });
  return groupOrUntagged.length ? groupOrUntagged : lines;
}

function parseParticipantFromNotes(notes) {
  const text = String(notes || '').trim();
  const tagged = text.match(/Participant:\s*([^|]+)/i);
  if (tagged) return [tagged[1].trim()].filter(Boolean);
  const group = text.match(/Participants:\s*([^|]+)/i);
  if (group) {
    return group[1]
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean);
  }
  return [];
}

function participantNamesFromSnapshot(receipt) {
  const snap = receipt?.snapshotJson ?? receipt?.snapshot_json;
  const list = snap?.participants ?? receipt?.groupParticipants;
  if (!Array.isArray(list) || !list.length) return [];
  return list
    .map((p) => {
      const fromParts = [p?.firstName, p?.lastName].filter(Boolean).join(' ').trim();
      return fromParts || String(p?.name || '').trim();
    })
    .filter(Boolean);
}

function participantNamesFromBooking(booking, { onlineOnly = false } = {}) {
  const groupMembers = Array.isArray(booking?.groupMembers) ? booking.groupMembers : [];
  if (groupMembers.length > 1) {
    return groupMembers.map((m) => String(m?.name || '').trim()).filter(Boolean);
  }
  if (onlineOnly && groupMembers.length <= 1) {
    // Group Razorpay on a single-member row — still show that traveller.
  }
  const travelers = Array.isArray(booking?.travelers) ? booking.travelers : [];
  if (travelers[0]) {
    const t = travelers[0];
    const name = [t.firstName, t.lastName].filter(Boolean).join(' ').trim();
    if (name) return [name];
  }
  const reg = booking?.registration;
  if (reg) {
    const name = [reg.firstName, reg.lastName].filter(Boolean).join(' ').trim();
    if (name) return [name];
  }
  return [];
}

function resolveHoverParticipantNames(receipt, booking) {
  const fromNotes = parseParticipantFromNotes(receipt?.notes);
  if (fromNotes.length) return fromNotes;

  const fromSnap = participantNamesFromSnapshot(receipt);
  if (fromSnap.length) return fromSnap;

  const legacyName = String(receipt?.name ?? '').trim();
  if (legacyName && !/^pay_/i.test(legacyName) && !/^order_/i.test(legacyName)) {
    return [legacyName];
  }

  const method = receiptMethodHoverLabel(receipt?.method ?? receipt?.paymentMode ?? receipt?.payment_mode);
  const isOnline = method === 'online' || method === 'razorpay';
  return participantNamesFromBooking(booking, { onlineOnly: isOnline });
}

function attachParticipantFields(line, receipt, booking) {
  const participantNames = resolveHoverParticipantNames(receipt, booking);
  return {
    ...line,
    participantNames,
    participantLabel: participantNames.join(', ')
  };
}

function receiptMethodHoverLabel(method) {
  const m = String(method || '').trim().toLowerCase();
  if (m === 'payment_gateway' || m === 'online' || m === 'razorpay' || m === 'upi' || m === 'card') {
    return 'online';
  }
  if (m === 'qr_bank') return 'qr/bank';
  if (m === 'cash' || m === 'office') return 'cash';
  return m || 'payment';
}

function linkedLegacyReceiptForBookingReceipt(receipt, booking) {
  const legacyId = receipt?.receiptId ?? receipt?.receipt_id;
  if (legacyId == null || String(legacyId).trim() === '') return null;
  return resolveLegacyRegistrationReceipts(booking).find(
    (row) => String(row?.id ?? '') === String(legacyId)
  );
}

function bookingReceiptHoverDetail(receipt, booking) {
  const notes = String(receipt?.notes || '').trim();
  if (notes) {
    const paymentId = notes.match(/Payment ID:\s*(\S+)/i);
    if (paymentId) return paymentId[1];
    return notes.replace(/^Participant:\s*[^|]+\s*/i, '').trim() || notes;
  }
  const legacy = linkedLegacyReceiptForBookingReceipt(receipt, booking);
  return legacy ? legacyReceiptHoverDetail(legacy) : '';
}

function legacyReceiptHoverDetail(receipt) {
  const paymentId = String(receipt?.paymentId ?? receipt?.payment_id ?? '').trim();
  if (paymentId) return paymentId;
  const name = String(receipt?.name ?? '').trim();
  if (name) return name;
  const manual = receipt?.manualReceiptJson ?? receipt?.manual_receipt_json;
  if (manual && typeof manual === 'object') {
    const bits = [
      manual.reference,
      manual.note,
      manual.remarks,
      manual.comment,
      manual.receipt_number,
      manual.receiptNumber
    ]
      .map((x) => String(x || '').trim())
      .filter(Boolean);
    if (bits.length) return bits.join(' | ');
  }
  if (typeof manual === 'string' && manual.trim()) return manual.trim();
  return '';
}


function resolvePaymentLink(detailText, receipt) {
  const fromDetail = extractRazorpayToken(detailText);
  const fromReceipt =
    extractRazorpayToken(receipt?.paymentId ?? receipt?.payment_id) ||
    extractRazorpayToken(receipt?.receiptNumber ?? receipt?.receipt_number);
  const paymentId = fromDetail || fromReceipt || '';
  return {
    paymentId,
    paymentLinkUrl: razorpayDashboardUrl(paymentId)
  };
}

function bookingReceiptToHoverLine(receipt, booking) {
  let method = receiptMethodHoverLabel(receipt?.method);
  const receiptNo = displayReceiptNumber(receipt);
  const legacy = linkedLegacyReceiptForBookingReceipt(receipt, booking);
  const detail = bookingReceiptHoverDetail(receipt, booking);
  const { paymentId, paymentLinkUrl } = resolvePaymentLink(detail, {
    ...receipt,
    paymentId: receipt?.paymentId ?? receipt?.payment_id ?? legacy?.paymentId ?? legacy?.payment_id
  });
  if (method === 'online' && paymentId) method = 'razorpay';
  const amount = Number(receipt?.amount ?? 0);
  const amountLabel = Number.isFinite(amount) && amount > 0 ? `${formatAdminInr(amount)}/-` : '—';
  return attachParticipantFields(
    {
      id: `br-${receipt?.id || receiptNo}`,
      method,
      receiptNo,
      detail,
      paymentId,
      paymentLinkUrl,
      amountLabel,
      printReceipt: receipt,
      booking
    },
    receipt,
    booking
  );
}

function legacyReceiptToHoverLine(receipt, booking) {
  let method = receiptMethodHoverLabel(receipt?.paymentMode ?? receipt?.payment_mode);
  const receiptNo = String(receipt?.id ?? '').trim() || '—';
  const detail = legacyReceiptHoverDetail(receipt);
  const { paymentId, paymentLinkUrl } = resolvePaymentLink(detail, receipt);
  if (method === 'online' && paymentId) method = 'razorpay';
  const amount = Number(receipt?.amount ?? 0);
  const amountLabel = Number.isFinite(amount) && amount > 0 ? `${formatAdminInr(amount)}/-` : '—';
  const linkedBookingReceipt = findBookingReceiptForLegacy(booking, receipt);
  const printReceipt = linkedBookingReceipt || legacyReceiptToPrintableRow(receipt, booking);
  return attachParticipantFields(
    {
      id: `lr-${receipt?.id || receiptNo}`,
      method,
      receiptNo,
      detail,
      paymentId,
      paymentLinkUrl,
      amountLabel,
      printReceipt,
      booking
    },
    receipt,
    booking
  );
}

/** Printable row when booking is paid but no booking_receipts row exists (legacy / import). */
export function synthesizeBookingPrintReceipt(booking) {
  const paidOnline = Number(booking?.amountPaidOnline ?? booking?.amount_paid_online ?? 0);
  const paidOffice = Number(booking?.amountPaidAtOffice ?? booking?.amount_paid_at_office ?? 0);
  const paid = paidOnline + paidOffice;
  if (!Number.isFinite(paid) || paid <= 0) return null;

  const reg = booking?.registration || {};
  const orderId = String(reg.orderId || reg.order_id || '').trim();
  const method = orderId.startsWith('order_') || paidOnline > 0 ? 'online' : 'office';

  return {
    id: `synth-${booking?.id || 'paid'}`,
    amount: paid,
    receiptNumber: String(booking?.bookingCode || booking?.booking_code || booking?.id || ''),
    paidAt: booking?.updatedAt || booking?.createdAt || null,
    createdAt: booking?.createdAt || null,
    method,
    notes: orderId ? `Order: ${orderId}` : null,
    booking
  };
}

function resolveLegacyRegistrationReceipts(booking) {
  const reg = booking?.registration;
  if (!reg) return [];
  if (Array.isArray(reg.legacyReceipts) && reg.legacyReceipts.length) {
    return reg.legacyReceipts.filter(Boolean);
  }
  const raw = Array.isArray(reg.receipts) ? reg.receipts.filter(Boolean) : [];
  return raw.filter((r) => r && r.bookingId == null && r.booking_id == null);
}

/** Booking + legacy Hasura receipts (old admin Payment Details). */
export function buildRegistrationPaymentHoverLines(booking) {
  const bookingReceipts = Array.isArray(booking?.receipts) ? booking.receipts.filter(Boolean) : [];
  const legacyReceipts = resolveLegacyRegistrationReceipts(booking);

  const linkedLegacyIds = new Set(
    bookingReceipts
      .map((r) => r?.receiptId ?? r?.receipt_id)
      .filter((id) => id != null && String(id).trim() !== '')
  );

  const items = [];
  for (const receipt of bookingReceipts) {
    items.push({
      sortAt: receipt?.paidAt || receipt?.createdAt,
      line: bookingReceiptToHoverLine(receipt, booking)
    });
  }
  for (const receipt of legacyReceipts) {
    if (linkedLegacyIds.has(receipt.id)) continue;
    items.push({
      sortAt: receipt?.createdAt,
      line: legacyReceiptToHoverLine(receipt, booking)
    });
  }

  items.sort((a, b) => new Date(b.sortAt || 0).getTime() - new Date(a.sortAt || 0).getTime());
  const lines = items.map((item) => item.line);
  if (lines.length) return lines;
  return buildFallbackPaymentHoverLines(booking);
}

/** When receipt rows are missing, derive a line from paid totals / order id (legacy Hasura data). */
function buildFallbackPaymentHoverLines(booking) {
  const reg = booking?.registration || {};
  const paidOnline = Number(booking?.amountPaidOnline ?? booking?.amount_paid_online ?? 0);
  const paidOffice = Number(booking?.amountPaidAtOffice ?? booking?.amount_paid_at_office ?? 0);
  const paid = paidOnline + paidOffice;
  if (!Number.isFinite(paid) || paid <= 0) return [];

  const legacy = resolveLegacyRegistrationReceipts(booking)[0];
  const orderId = String(reg.orderId || reg.order_id || '').trim();
  const detail =
    orderId ||
    (legacy ? legacyReceiptHoverDetail(legacy) : '') ||
    String(legacy?.paymentId || legacy?.payment_id || '').trim();

  let method = 'payment';
  if (orderId.startsWith('order_')) method = 'razorpay';
  else if (paidOnline > 0) method = 'online';
  else if (paidOffice > 0) method = 'cash';

  const receiptNo =
    (legacy?.id != null ? String(legacy.id) : '') ||
    String(booking?.bookingCode || booking?.booking_code || '').trim() ||
    '—';
  const { paymentId, paymentLinkUrl } = resolvePaymentLink(detail, legacy);
  if (method === 'online' && paymentId) method = 'razorpay';
  const linkedBookingReceipt = legacy ? findBookingReceiptForLegacy(booking, legacy) : null;
  const bookingReceipts = Array.isArray(booking?.receipts) ? booking.receipts.filter(Boolean) : [];
  const printReceipt =
    linkedBookingReceipt ||
    (legacy ? legacyReceiptToPrintableRow(legacy, booking) : null) ||
    bookingReceipts[0] ||
    synthesizeBookingPrintReceipt(booking);

  return [
    attachParticipantFields(
      {
        id: `fallback-${booking?.id || receiptNo}`,
        method,
        receiptNo,
        detail,
        paymentId,
        paymentLinkUrl,
        amountLabel: `${formatAdminInr(paid)}/-`,
        printReceipt,
        booking
      },
      printReceipt || legacy,
      booking
    )
  ];
}

/** Admin + legacy registration comments. */
export function buildRegistrationCommentHoverLines(booking) {
  return buildRegistrationCommentLines(booking);
}

export function buildRegistrationStatusHoverContent(booking, traveler = null, travelerIndex = 0) {
  const paymentLines = buildRegistrationPaymentHoverLinesForParticipant(booking, traveler, travelerIndex);
  const commentLines = buildRegistrationCommentHoverLines(booking);
  return {
    paymentLines,
    commentLines,
    hasContent: true
  };
}
